import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Price from '../../../../components/stateless/Price';
import Translation from '../../../../components/stateless/Translation';
import intlPath from '../../../../../src/web/utils/intlPath';
import SMTooltip from '../SMTooltip';
import { CA, GB } from '../../../../../src/shared/i18n';

const LOCK_FEES = { [CA]: 26.0, [GB]: 16.5 };
const DEFAULT_LOCK_FEE = 19.99;
const ADMIN_FEE = 29.0;

const propTypes = {
  monthlyRate: PropTypes.number.isRequired,
  fullMonthlyRate: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  discountPlans: PropTypes.arrayOf(PropTypes.shape({
    autoApply: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    discountPlanDiscounts: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
    })),
  })),
  surchargeEnabled: PropTypes.bool,
  surchargePercentage: PropTypes.number,
  showDetailedFootnotes: PropTypes.bool,
  taxesNote: PropTypes.node,
  isParking: PropTypes.bool,
  useWeeklyRates: PropTypes.bool,
  useLockTooltip: PropTypes.bool,
  country: PropTypes.string,
};

const defaultProps = {
  discountPlans: [],
  surchargeEnabled: false,
  surchargePercentage: 0,
  showDetailedFootnotes: true,
  taxesNote: null,
  isParking: false,
  useWeeklyRates: false,
  useLockTooltip: false,
  country: null,
};

const SMMoveInCostBreakdown = ({
  monthlyRate,
  fullMonthlyRate,
  currency,
  discountPlans,
  surchargeEnabled,
  surchargePercentage,
  showDetailedFootnotes,
  taxesNote,
  isParking,
  useWeeklyRates,
  useLockTooltip,
  country,
}) => {
  const { locale } = useIntl();
  const monthlyRateNum = parseFloat(monthlyRate) || 0;
  const fullMonthlyRateNum = parseFloat(fullMonthlyRate) || 0;
  const autoApplyPlan = discountPlans?.find(p => p.autoApply);
  const promoMonths = autoApplyPlan?.discountPlanDiscounts?.length ?? 0;
  const discountAmount = fullMonthlyRateNum - monthlyRateNum;
  const hasPromo = discountAmount > 0 && !!autoApplyPlan;
  const lockFee = isParking ? 0 : (LOCK_FEES[country] ?? DEFAULT_LOCK_FEE);
  const processingFee = surchargeEnabled
    ? (monthlyRateNum + lockFee + ADMIN_FEE) * (surchargePercentage / 100)
    : 0;
  const estimatedTotal = monthlyRateNum + lockFee + ADMIN_FEE + processingFee;
  const totalSavings = hasPromo && promoMonths > 0 ? discountAmount * promoMonths : null;

  return (
    <div className="text-left">
      <div className="flex justify-between mb-3">
        <div>
          <Translation id="widgets.moveInCostBreakdown.monthlyRate" defaultMessage="Monthly Rate" />
          {showDetailedFootnotes && <sup>*</sup>}
          {useWeeklyRates && (
            <div className="text-sm text-[#565958]">
              <Translation
                id="widgets.moveInCostBreakdown.weeklyRate"
                defaultMessage="{weeklyRate} per week"
                values={{
                  weeklyRate: (
                    <Price
                      currency={currency}
                      locale={locale}
                      value={fullMonthlyRateNum / 4.33}
                      currencyDisplay="narrowSymbol"
                    />
                  ),
                }}
              />
            </div>
          )}
        </div>
        <div className="text-right">
          <Price
            currency={currency}
            value={fullMonthlyRateNum}
            locale={locale}
            currencyDisplay="narrowSymbol"
          />
        </div>
      </div>

      {hasPromo && (
        <div className="flex justify-between mb-3">
          <div>
            <div className="flex items-center gap-1 text-[#2a8703]">
              <i className="fa fa-tag" aria-hidden="true" />
              <Translation id={`widgets.unitstablev2.storageMart.unitPromo.${autoApplyPlan?.name}`} />
            </div>
          </div>
          <div className="bg-[#e8f5e9] text-[#2a8703] px-2 py-0.5 rounded font-medium whitespace-nowrap self-center">
            -<Price
              currency={currency}
              locale={locale}
              value={discountAmount}
              currencyDisplay="narrowSymbol"
            />
          </div>
        </div>
      )}

      <div className="mb-3">
        <div className="mb-2">
          <Translation id="widgets.moveInCostBreakdown.oneTimeFees" defaultMessage="One-time Fees" />
        </div>
        <div className="border-l-2 border-gray-300 pl-3 ml-1 flex flex-col gap-2">
          {!isParking && (
            <div className="flex justify-between">
              <div className="flex items-center gap-1">
                <Translation id="widgets.moveInCostBreakdown.lockFee" defaultMessage="Lock" />
                {useLockTooltip ? (
                  <span className="relative top-[0px]">
                    <SMTooltip
                      title={<Translation id="widgets.moveInCostBreakdown.lockFee" defaultMessage="Lock" />}
                      body={<Translation id="widgets.moveInCostBreakdown.footnoteLock" defaultMessage="One time lock fee - The fee covers a new lock for each door. Bring your own and return ours unopened for a refund." />}
                    />
                  </span>
                ) : (
                  <sup>†</sup>
                )}
              </div>
              <Price
                currency={currency}
                value={lockFee}
                locale={locale}
                currencyDisplay="narrowSymbol"
              />
            </div>
          )}
          <div className="flex justify-between">
            <div><Translation id="widgets.moveInCostBreakdown.adminFee" defaultMessage="Admin" /></div>
            <div>
              <Price
                currency={currency}
                value={ADMIN_FEE}
                locale={locale}
                currencyDisplay="narrowSymbol"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between mb-3">
        <Translation id="widgets.moveInCostBreakdown.taxes" defaultMessage="Taxes" />
        <span className="text-[#565958]">
          {taxesNote || (
            <Translation
              id="widgets.moveInCostBreakdown.taxesValue"
              defaultMessage="Calculated at Checkout"
            />
          )}
        </span>
      </div>

      {surchargeEnabled && (
        <div className="flex justify-between mb-3">
          <div>
            <Translation
              id="widgets.moveInCostBreakdown.processingFee"
              defaultMessage="{surchargePercentage}% Processing Fee"
              values={{ surchargePercentage }}
            />
            <sup>**</sup>
          </div>
          <Price
            currency={currency}
            value={processingFee}
            locale={locale}
            currencyDisplay="narrowSymbol"
          />
        </div>
      )}

      <hr className="my-3 border-gray-300" />

      <div className="flex justify-between font-bold mb-3">
        <Translation
          id="widgets.moveInCostBreakdown.estimatedTotal"
          defaultMessage="Estimated 1st Month Total"
        />
        <div>
          <Price
            currency={currency}
            value={estimatedTotal}
            locale={locale}
            currencyDisplay="narrowSymbol"
          />
          {!isParking && <sup>{showDetailedFootnotes ? '††' : '*'}</sup>}
        </div>
      </div>

      {totalSavings && (
        <div className="bg-[#e8f5e9] text-center text-[#2a8703] p-3 rounded mb-4">
          <Translation
            id="widgets.moveInCostBreakdown.savingsBanner"
            defaultMessage="Your {promoMonths} Months Savings {savings}"
            values={{
              promoMonths,
              savings: (
                <Price
                  currency={currency}
                  value={totalSavings}
                  locale={locale}
                  currencyDisplay="narrowSymbol"
                />
              ),
            }}
          />
        </div>
      )}

      <div className="text-xs text-[#565958] flex flex-col gap-2">
        {showDetailedFootnotes && (
          <p>
            <sup>*</sup>
            {' '}
            <Translation
              id="widgets.moveInCostBreakdown.footnoteRate"
              defaultMessage="First month rate and promotion will be prorated based on your selected move-in date."
            />
          </p>
        )}
        {!isParking && !useLockTooltip && (
          <p>
            <sup>†</sup>
            {' '}
            <Translation
              id="widgets.moveInCostBreakdown.footnoteLock"
              defaultMessage="One time lock fee - The fee covers a new lock for each door. Bring your own and return ours unopened for a refund."
            />
          </p>
        )}
        {!isParking && (
          <p>
            <sup>{showDetailedFootnotes ? '††' : '*'}</sup>
            {' '}
            <Translation
              id="widgets.moveInCostBreakdown.footnoteTotal"
              defaultMessage="Rate includes up to two free months of {valueCoverage}."
              values={{
                valueCoverage: (
                  <a href={intlPath(locale, '/about-us/value-coverage')} className="!text-[#007a7c] underline">
                    <Translation
                      id="widgets.moveInCostBreakdown.valueCoverage"
                      defaultMessage="Value Coverage"
                    />
                  </a>
                ),
              }}
            />
          </p>
        )}
        {surchargeEnabled && (
          <p>
            <sup>**</sup>
            {' '}
            <Translation
              id="widgets.moveInCostBreakdown.footnoteProcessing"
              defaultMessage="A processing fee of {surchargePercentage}% will be added to all credit card payments. No fee for ACH bank transfer."
              values={{ surchargePercentage }}
            />
          </p>
        )}
      </div>
    </div>
  );
};

SMMoveInCostBreakdown.propTypes = propTypes;
SMMoveInCostBreakdown.defaultProps = defaultProps;

export default SMMoveInCostBreakdown;
